import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";
import {
  FaUser,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChartBar,
  FaCrown,
  FaFlag,
  FaEnvelope,
  FaMoon,
  FaSun,
  FaHome,
  FaBook,
  FaInfoCircle,
  FaPhoneAlt,
  FaChevronDown,
} from "react-icons/fa";
import { MdDashboard, MdUpload } from "react-icons/md";
import { logout } from "../../store/slices/authSlice";
import { useDarkMode } from "../../context/DarkModeContext";
import { buildApiUrl } from "../../utils/apiConfig";
import toast from "react-hot-toast";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [imageError, setImageError] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Logged out successfully");
    navigate("/");
    setIsOpen(false);
    setIsProfileOpen(false);
  };

  const getProfileImageUrl = (profilePicture, name) => {
    if (!profilePicture || imageError[profilePicture]) {
      const initials =
        name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2) || "U";

      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%238b5cf6'/%3E%3Ctext x='20' y='25' font-size='18' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
    }
    if (profilePicture?.startsWith("http")) {
      return profilePicture;
    }
    if (profilePicture?.startsWith("data:")) {
      return profilePicture;
    }
    return buildApiUrl(profilePicture);
  };

  const handleImageError = (key) => {
    setImageError((prev) => ({ ...prev, [key]: true }));
  };

  // Animation variants
  const navVariants = {
    hidden: { y: -100 },
    visible: {
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, x: "100%" },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
    exit: {
      opacity: 0,
      x: "100%",
      transition: { duration: 0.3 },
    },
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  const navLinks = [
    { name: "Home", path: "/", icon: <FaHome size={14} /> },
    { name: "Notes", path: "/notes", icon: <FaBook size={14} /> },
    { name: "About", path: "/about", icon: <FaInfoCircle size={14} /> },
    { name: "Contact", path: "/contact", icon: <FaPhoneAlt size={14} /> },
  ];

  // Dynamic styles based on dark mode and scroll
  const getNavbarStyle = () => ({
    background: scrolled
      ? isDarkMode
        ? "rgba(15, 15, 26, 0.95)"
        : "rgba(255, 255, 255, 0.95)"
      : isDarkMode
        ? "rgba(15, 15, 26, 0.8)"
        : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(20px)",
    borderBottom: `1px solid ${
      scrolled
        ? isDarkMode
          ? "rgba(139, 92, 246, 0.2)"
          : "rgba(139, 92, 246, 0.1)"
        : "transparent"
    }`,
    position: "sticky",
    top: 0,
    zIndex: 1000,
    width: "100%",
    transition: "all 0.3s ease",
  });

  // Mobile Menu Component
  const MobileMenu = () => (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
            }}
          />

          {/* Menu Panel */}
          <motion.div
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "280px",
              background: isDarkMode ? "#1a1a2e" : "#ffffff",
              boxShadow: isDarkMode
                ? "-5px 0 25px rgba(0,0,0,0.3)"
                : "-5px 0 25px rgba(0,0,0,0.1)",
              zIndex: 10000,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "1.25rem",
                borderBottom: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "1.3rem",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: "#8b5cf6" }}>VTU</span>
                <span style={{ color: isDarkMode ? "#f1f5f9" : "#1e293b" }}>
                  GenX
                </span>
              </span>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  border: "none",
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTimes size={14} color="white" />
              </button>
            </div>

            {/* User Profile Section */}
            {isAuthenticated && user && (
              <div
                style={{
                  padding: "1rem",
                  borderBottom: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                  marginBottom: "0.5rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.5rem",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    borderRadius: "12px",
                  }}
                >
                  <img
                    src={getProfileImageUrl(user?.profilePicture, user?.name)}
                    alt="Profile"
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #8b5cf6",
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                    >
                      {user?.name}
                    </div>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: user?.role === "admin" ? "#f59e0b" : "#8b5cf6",
                      }}
                    >
                      {user?.role === "admin"
                        ? "Administrator"
                        : user?.role === "moderator"
                          ? "Moderator"
                          : "Student"}{" "}
                      • {user?.branch || "VTU"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              style={{
                margin: "0 1rem 1rem 1rem",
                padding: "0.6rem",
                background: isDarkMode
                  ? "rgba(139,92,246,0.15)"
                  : "rgba(139,92,246,0.08)",
                border: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                borderRadius: "10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                fontSize: "0.85rem",
              }}
            >
              {isDarkMode ? <FaSun size={14} /> : <FaMoon size={14} />}
              {isDarkMode ? "Light Mode" : "Dark Mode"}
            </button>

            {/* ACCOUNT SECTION - AT THE TOP */}
            {isAuthenticated && user && (
              <div
                style={{
                  padding: "0 1rem",
                  marginBottom: "0.5rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    padding: "0.5rem 0.5rem",
                  }}
                >
                  ACCOUNT
                </div>

                <Link
                  to="/dashboard"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <MdDashboard size={18} color="#8b5cf6" /> Dashboard
                </Link>

                <Link
                  to="/upload"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <MdUpload size={18} color="#8b5cf6" /> Upload Notes
                </Link>

                <Link
                  to="/profile"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <FaUser size={16} color="#8b5cf6" /> Profile
                </Link>

                <Link
                  to="/my-tickets"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <FaEnvelope size={16} color="#8b5cf6" /> My Tickets
                </Link>

                {/* Logout Button - Inside Account Section */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: "#ef4444",
                    background: "none",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginTop: "0.25rem",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode
                      ? "rgba(239,68,68,0.1)"
                      : "rgba(239,68,68,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <FaSignOutAlt size={16} /> Logout
                </button>
              </div>
            )}

            {/* Admin Section */}
            {isAuthenticated && user?.role === "admin" && (
              <div
                style={{
                  padding: "0 1rem",
                  marginTop: "0.5rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#f59e0b",
                    padding: "0.5rem 0.5rem",
                    borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.05)"}`,
                  }}
                >
                  ADMIN
                </div>
                <Link
                  to="/admin"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: "#f59e0b",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <FaChartBar size={16} /> Admin Dashboard
                  <span
                    style={{
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      color: "white",
                      fontSize: "0.6rem",
                      padding: "0.2rem 0.5rem",
                      borderRadius: "20px",
                      marginLeft: "auto",
                    }}
                  >
                    Admin
                  </span>
                </Link>
              </div>
            )}

            {/* Moderator Section */}
            {isAuthenticated && user?.role === "moderator" && (
              <div
                style={{
                  padding: "0 1rem",
                  marginTop: "0.5rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#8b5cf6",
                    padding: "0.5rem 0.5rem",
                    borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.05)"}`,
                  }}
                >
                  MODERATOR
                </div>
                <Link
                  to="/moderator"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: "#8b5cf6",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <FaFlag size={16} /> Moderator Dashboard
                  <span
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      color: "white",
                      fontSize: "0.6rem",
                      padding: "0.2rem 0.5rem",
                      borderRadius: "20px",
                      marginLeft: "auto",
                    }}
                  >
                    Mod
                  </span>
                </Link>
              </div>
            )}

            {/* Main Navigation Section - At the bottom */}
            <div
              style={{
                padding: "0 1rem",
                marginTop: "0.5rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  padding: "0.5rem 0.5rem",
                  borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.05)"}`,
                }}
              >
                MAIN NAVIGATION
              </div>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 0.75rem",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    marginBottom: "0.25rem",
                  }}
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                  }}
                >
                  <span style={{ color: "#8b5cf6", width: "20px" }}>
                    {link.icon}
                  </span>
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Login/Register Section - When not logged in */}
            {!isAuthenticated && (
              <div
                style={{
                  padding: "0 1rem",
                  marginTop: "0.5rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    padding: "0.5rem 0.5rem",
                  }}
                >
                  ACCOUNT
                </div>
                <Link
                  to="/login"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.75rem",
                    borderRadius: "10px",
                    textDecoration: "none",
                    color: "#8b5cf6",
                    border: "1px solid #8b5cf6",
                    textAlign: "center",
                    marginBottom: "0.5rem",
                    fontSize: "0.9rem",
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.75rem",
                    borderRadius: "10px",
                    textDecoration: "none",
                    color: "white",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    textAlign: "center",
                    fontSize: "0.9rem",
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  Register
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <motion.nav
        variants={navVariants}
        initial="hidden"
        animate="visible"
        style={getNavbarStyle()}
      >
        {/* Static gradient border */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
            opacity: scrolled ? 1 : 0,
            transition: "opacity 0.3s",
          }}
        />

        <div
          className="nav-container"
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "0.75rem 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Logo */}
          <Link
            to="/"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: isMobile ? "1.5rem" : "1.8rem",
                fontWeight: "bold",
                letterSpacing: "1px",
              }}
            >
              <span style={{ color: "#8b5cf6" }}>VTU</span>
              <span
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                GenX
              </span>
            </span>
          </Link>

          {/* Desktop Menu */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
              <div style={{ display: "flex", gap: "1.5rem" }}>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    style={{
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                      textDecoration: "none",
                      fontSize: "0.95rem",
                      fontWeight: "500",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = "#8b5cf6";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = isDarkMode ? "#cbd5e1" : "#475569";
                    }}
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* Dark Mode Toggle Button */}
              <button
                onClick={toggleDarkMode}
                style={{
                  background: isDarkMode
                    ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                    : "linear-gradient(135deg, #f59e0b, #d97706)",
                  border: "none",
                  borderRadius: "12px",
                  width: "38px",
                  height: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  color: "white",
                  fontSize: "1rem",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                title={
                  isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
                }
              >
                {isDarkMode ? <FaSun /> : <FaMoon />}
              </button>

              {isAuthenticated && user ? (
                <div ref={dropdownRef} style={{ position: "relative" }}>
                  <div
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      cursor: "pointer",
                      padding: "0.4rem 0.8rem 0.4rem 0.4rem",
                      borderRadius: "40px",
                      background: isDarkMode
                        ? "rgba(139,92,246,0.15)"
                        : "rgba(139,92,246,0.08)",
                      transition: "all 0.3s",
                      border:
                        user?.role === "admin"
                          ? "2px solid #f59e0b"
                          : "1px solid rgba(139,92,246,0.3)",
                    }}
                  >
                    {user?.role === "admin" && (
                      <FaCrown
                        color="#f59e0b"
                        size={14}
                        style={{ marginRight: "0.2rem" }}
                      />
                    )}
                    <img
                      src={getProfileImageUrl(user?.profilePicture, user?.name)}
                      alt="Profile"
                      onError={() => handleImageError(user?.profilePicture)}
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #8b5cf6",
                      }}
                    />
                    <span
                      style={{
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                        fontWeight: "500",
                        fontSize: "0.9rem",
                      }}
                    >
                      {user?.name?.split(" ")[0] || "User"}
                    </span>
                    <FaChevronDown
                      size={12}
                      style={{
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                        transition: "transform 0.3s",
                        transform: isProfileOpen
                          ? "rotate(180deg)"
                          : "rotate(0)",
                      }}
                    />
                  </div>

                  <AnimatePresence>
                    {isProfileOpen && (
                      <motion.div
                        variants={dropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{
                          position: "absolute",
                          top: "calc(100% + 10px)",
                          right: 0,
                          background: isDarkMode
                            ? "rgba(30, 30, 45, 0.98)"
                            : "rgba(255, 255, 255, 0.98)",
                          backdropFilter: "blur(20px)",
                          borderRadius: "20px",
                          boxShadow: isDarkMode
                            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                            : "0 25px 50px -12px rgba(0,0,0,0.25)",
                          width: "280px",
                          overflow: "hidden",
                          zIndex: 1001,
                          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                        }}
                      >
                        {/* User info header */}
                        <div
                          style={{
                            padding: "1.25rem",
                            background: isDarkMode
                              ? "rgba(139,92,246,0.1)"
                              : "rgba(139,92,246,0.05)",
                            borderBottom: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "60px",
                              height: "60px",
                              margin: "0 auto 0.75rem",
                              borderRadius: "50%",
                              overflow: "hidden",
                              border: "3px solid #8b5cf6",
                            }}
                          >
                            <img
                              src={getProfileImageUrl(
                                user?.profilePicture,
                                user?.name,
                              )}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              fontWeight: "bold",
                              color: isDarkMode ? "#f1f5f9" : "#1e293b",
                              fontSize: "1rem",
                            }}
                          >
                            {user?.name}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color:
                                user?.role === "admin" ? "#f59e0b" : "#8b5cf6",
                              marginTop: "0.25rem",
                            }}
                          >
                            {user?.role === "admin"
                              ? "Administrator"
                              : user?.role === "moderator"
                                ? "Moderator"
                                : "Student"}
                          </div>
                          <div
                            style={{
                              fontSize: "0.7rem",
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              marginTop: "0.25rem",
                            }}
                          >
                            {user?.email}
                          </div>
                        </div>

                        {/* Navigation Links */}
                        {[
                          {
                            to: "/dashboard",
                            icon: <MdDashboard size={16} />,
                            label: "Dashboard",
                            color: "#8b5cf6",
                          },
                          {
                            to: "/upload",
                            icon: <MdUpload size={16} />,
                            label: "Upload Notes",
                            color: "#8b5cf6",
                          },
                          {
                            to: "/profile",
                            icon: <FaUser size={14} />,
                            label: "Profile",
                            color: "#8b5cf6",
                          },
                          {
                            to: "/my-tickets",
                            icon: <FaEnvelope size={14} />,
                            label: "My Tickets",
                            color: "#8b5cf6",
                          },
                        ].map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              transition: "all 0.3s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = isDarkMode
                                ? "rgba(139,92,246,0.1)"
                                : "rgba(139,92,246,0.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <Link
                              to={item.to}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                padding: "0.75rem 1.25rem",
                                color: isDarkMode ? "#cbd5e1" : "#475569",
                                textDecoration: "none",
                                transition: "all 0.3s",
                                fontSize: "0.9rem",
                              }}
                              onClick={() => setIsProfileOpen(false)}
                            >
                              <span style={{ color: item.color }}>
                                {item.icon}
                              </span>
                              {item.label}
                            </Link>
                          </div>
                        ))}

                        {/* Admin/Moderator Links */}
                        {(user?.role === "admin" ||
                          user?.role === "moderator") && (
                          <>
                            <div
                              style={{
                                height: "1px",
                                background: isDarkMode
                                  ? "rgba(139,92,246,0.2)"
                                  : "rgba(139,92,246,0.1)",
                                margin: "0.5rem 0",
                              }}
                            />
                            {user?.role === "admin" && (
                              <div
                                style={{
                                  transition: "all 0.3s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "rgba(139,92,246,0.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    "transparent";
                                }}
                              >
                                <Link
                                  to="/admin"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    padding: "0.75rem 1.25rem",
                                    color: "#f59e0b",
                                    textDecoration: "none",
                                    fontSize: "0.9rem",
                                    fontWeight: 500,
                                  }}
                                  onClick={() => setIsProfileOpen(false)}
                                >
                                  <FaChartBar size={16} /> Admin Dashboard
                                  <span
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #f59e0b, #d97706)",
                                      color: "white",
                                      fontSize: "0.65rem",
                                      padding: "0.2rem 0.6rem",
                                      borderRadius: "20px",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    Admin
                                  </span>
                                </Link>
                              </div>
                            )}
                            {user?.role === "moderator" && (
                              <div
                                style={{
                                  transition: "all 0.3s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "rgba(139,92,246,0.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    "transparent";
                                }}
                              >
                                <Link
                                  to="/moderator"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    padding: "0.75rem 1.25rem",
                                    color: "#8b5cf6",
                                    textDecoration: "none",
                                    fontSize: "0.9rem",
                                    fontWeight: 500,
                                  }}
                                  onClick={() => setIsProfileOpen(false)}
                                >
                                  <FaFlag size={16} /> Moderator Dashboard
                                  <span
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                                      color: "white",
                                      fontSize: "0.65rem",
                                      padding: "0.2rem 0.6rem",
                                      borderRadius: "20px",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    Mod
                                  </span>
                                </Link>
                              </div>
                            )}
                          </>
                        )}

                        <div
                          style={{
                            height: "1px",
                            background: isDarkMode
                              ? "rgba(139,92,246,0.2)"
                              : "rgba(139,92,246,0.1)",
                            margin: "0.5rem 0",
                          }}
                        />

                        <button
                          onClick={handleLogout}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            padding: "0.75rem 1.25rem",
                            color: "#ef4444",
                            background: "none",
                            border: "none",
                            width: "100%",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            transition: "all 0.3s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDarkMode
                              ? "rgba(239,68,68,0.1)"
                              : "rgba(239,68,68,0.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <FaSignOutAlt size={14} /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <Link
                    to="/login"
                    style={{
                      padding: "0.5rem 1.25rem",
                      borderRadius: "40px",
                      textDecoration: "none",
                      color: "#8b5cf6",
                      border: "2px solid #8b5cf6",
                      fontWeight: "500",
                      fontSize: "0.9rem",
                      transition: "all 0.3s",
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#8b5cf6";
                      e.target.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.color = "#8b5cf6";
                    }}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    style={{
                      padding: "0.5rem 1.25rem",
                      borderRadius: "40px",
                      textDecoration: "none",
                      color: "white",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      fontWeight: "500",
                      fontSize: "0.9rem",
                      transition: "all 0.3s",
                      boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
                    }}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              className="mobile-menu-btn"
              onClick={() => setIsOpen(!isOpen)}
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                border: "none",
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1002,
                boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
              }}
            >
              {isOpen ? (
                <FaTimes size={20} color="white" />
              ) : (
                <FaBars size={20} color="white" />
              )}
            </button>
          )}
        </div>
      </motion.nav>

      {/* Mobile Menu - Rendered outside nav using React Portal */}
      {isMobile && ReactDOM.createPortal(<MobileMenu />, document.body)}
    </>
  );
};

export default Navbar;
